import { z } from 'zod';
import { StorageTerms } from '../storage-terms.js';
import { Id, LongText, Money, ShortText, Timestamp } from './common.js';
import { CustomerInput, Mileage, PlannedSlot, PlannedWork, VehicleFields } from './dossiers.js';

/**
 * Vague 6 — un seul parcours d'arrivée : **intervention planifiée** (ajout 11) → **entrée du véhicule**
 * → **fiche de réception** (ajout 10), qui atteste de l'état du véhicule à la remise.
 */

// ─── Fiche de réception (ajout 10) ──────────────────────────────────────────

/** Niveau de carburant ou de charge en huitièmes (0 à 8) ; `null` = « non relevé ». */
export const FuelLevel = z.int().min(0).max(8).nullable();
export const FUEL_LEVEL_STEPS = 8;

/** État d'un point de contrôle : « RAS », « remarque » ou « non vérifié ». */
export const ChecklistState = z.enum(['ok', 'remark', 'not_checked']);
export type ChecklistState = z.infer<typeof ChecklistState>;
export const CHECKLIST_STATE_LABELS: Record<ChecklistState, string> = {
  ok: 'RAS',
  remark: 'Remarque',
  not_checked: 'Non vérifié',
};

export const ChecklistKey = z.string().regex(/^[a-z0-9_]{1,40}$/);
/** Point de la liste de contrôle d'un garage (réglage `entryChecklist`). */
export const EntryChecklistItem = z.strictObject({ key: ChecklistKey, label: ShortText.max(80) });
export type EntryChecklistItem = z.infer<typeof EntryChecklistItem>;
export const ENTRY_CHECKLIST_MAX = 30;
/** Réglage du garage : 1 à 30 points, clés uniques. */
export const EntryChecklist = z
  .array(EntryChecklistItem)
  .min(1)
  .max(ENTRY_CHECKLIST_MAX)
  .refine((items) => new Set(items.map((i) => i.key)).size === items.length, 'Clés en double');

/** Liste par défaut, générique (réglage `entryChecklist` à `null`). */
export const DEFAULT_ENTRY_CHECKLIST: readonly EntryChecklistItem[] = [
  { key: 'bodywork', label: 'Carrosserie' },
  { key: 'wheels_tyres', label: 'Jantes et pneus' },
  { key: 'glazing', label: 'Vitrages' },
  { key: 'lights', label: 'Optiques' },
  { key: 'interior', label: 'Intérieur' },
  { key: 'personal_items', label: 'Objets personnels laissés' },
  { key: 'keys', label: 'Nombre de clés' },
  { key: 'documents', label: 'Documents du véhicule' },
  { key: 'warning_lights', label: 'Voyants allumés au tableau de bord' },
];

/** Zones du schéma du véhicule. */
export const DamageZone = z.enum(['front', 'rear', 'left', 'right', 'roof', 'interior']);
export type DamageZone = z.infer<typeof DamageZone>;
export const DAMAGE_ZONE_LABELS: Record<DamageZone, string> = {
  front: 'Avant',
  rear: 'Arrière',
  left: 'Côté gauche',
  right: 'Côté droit',
  roof: 'Toit',
  interior: 'Intérieur',
};

const ShortComment = z.string().trim().max(500);

/** Point contrôlé dans une fiche : libellé **copié** du réglage (figé avec la fiche). */
export const EntryChecklistResult = z.object({
  key: ChecklistKey,
  label: z.string(),
  state: ChecklistState,
  comment: z.string().nullable(),
});
export type EntryChecklistResult = z.infer<typeof EntryChecklistResult>;

/** Dommage repéré, par zone, avec ses photos (`entry_condition`, même dossier). */
export const EntryDamage = z.strictObject({
  zone: DamageZone,
  comment: ShortComment.min(1),
  mediaIds: z.array(Id).max(10).default([]),
});
export type EntryDamage = z.infer<typeof EntryDamage>;

/**
 * Brouillon (création ou mise à jour partielle). Le kilométrage vient de l'entrée (`entryMileage`) ; les
 * libellés des points viennent du réglage. Photos : `entry_condition` du même dossier (sinon 422).
 */
export const EntryReportDraftInput = z.strictObject({
  fuelLevel: FuelLevel.optional(),
  checklist: z
    .array(z.strictObject({ key: ChecklistKey, state: ChecklistState, comment: ShortComment.nullable().optional() }))
    .max(ENTRY_CHECKLIST_MAX)
    .optional(),
  damages: z.array(EntryDamage).max(30).optional(),
  generalNotes: LongText.max(2000).nullable().optional(),
  mediaIds: z.array(Id).max(50).optional(),
});

/**
 * Validation : la fiche est figée, numérotée, versionnée, empreinte sha256 (contenu et photos), PDF généré.
 * `sendToCustomer` : partage immédiat (e-mail sans pièce jointe + événement dans la conversation) ; exige
 * en plus `media.publish` (403 sinon).
 */
export const EntryReportValidateInput = z.strictObject({
  version: z.int().positive(),
  sendToCustomer: z.boolean().default(false),
});

/** Contenu figé d'une version (c'est lui que couvre l'empreinte, sous forme JSON canonique). */
export const EntryReportContent = z.object({
  mileage: z.int(),
  fuelLevel: FuelLevel,
  checklist: z.array(EntryChecklistResult),
  damages: z.array(z.object({ zone: DamageZone, comment: z.string(), mediaIds: z.array(Id) })),
  generalNotes: z.string().nullable(),
  mediaIds: z.array(Id),
  /** Conditions de gardiennage figées à l'entrée : la prise de connaissance les couvre aussi. */
  storageTerms: StorageTerms,
  receivedAt: Timestamp,
});
export type EntryReportContent = z.infer<typeof EntryReportContent>;

export const EntryReportStatus = z.enum(['draft', 'validated', 'superseded']);

/** Prise de connaissance : portail (bouton) ou sur place (signature dessinée à l'accueil). */
export const AcknowledgmentChannel = z.enum(['portal', 'in_person']);
export const EntryReportAcknowledgmentOutput = z.object({ at: Timestamp, channel: AcknowledgmentChannel });

export const EntryReportOutput = z.object({
  id: Id,
  interventionId: Id,
  /** Numéro de la fiche dans le garage (attribué à la première validation, gardé par les versions suivantes). */
  number: z.int().nullable(),
  version: z.int(),
  status: EntryReportStatus,
  content: EntryReportContent,
  /** Membre qui a fait la réception et date (serveur). */
  receivedByName: z.string().nullable(),
  createdAt: Timestamp,
  validatedAt: Timestamp.nullable(),
  validatedByName: z.string().nullable(),
  /** Empreinte sha256 (hex) du contenu canonique et des photos, à la validation. */
  contentSha256: z.string().nullable(),
  pdf: z.object({ available: z.boolean(), sha256: z.string().nullable() }),
  signature: z.boolean(),
  sharedAt: Timestamp.nullable(),
  acknowledgment: EntryReportAcknowledgmentOutput.nullable(),
  /** Toutes les versions de la fiche du dossier, la plus récente d'abord. */
  versions: z.array(z.object({ version: z.int(), status: EntryReportStatus, validatedAt: Timestamp.nullable() })),
});
export type EntryReportOutput = z.infer<typeof EntryReportOutput>;

export const EntryReportVersionParams = z.strictObject({ interventionId: Id, version: z.coerce.number().int().positive() });

/** Fiche vue par le client : dernière version validée **partagée**, sans brouillon ni identifiant interne. */
export const PortalEntryReportOutput = z.object({
  number: z.int(),
  version: z.int(),
  content: EntryReportContent,
  receivedByName: z.string().nullable(),
  validatedAt: Timestamp,
  contentSha256: z.string(),
  pdfAvailable: z.boolean(),
  acknowledgment: EntryReportAcknowledgmentOutput.nullable(),
  /** Vrai tant que le client peut déclarer en avoir pris connaissance (version courante, pas encore fait). */
  acknowledgeable: z.boolean(),
});
export type PortalEntryReportOutput = z.infer<typeof PortalEntryReportOutput>;

/**
 * « J'ai pris connaissance de l'état constaté » (et des conditions de gardiennage figées) : liée à la
 * version exacte, idempotente (`idempotencyKey`), horodatée par le serveur. Un silence n'en est jamais une.
 */
export const PortalEntryReportAcknowledgeInput = z.strictObject({ version: z.int().positive(), idempotencyKey: Id });

/** Signature sur place (multipart, partie `file`, PNG ou JPEG, 1 Mo) : sur le brouillon seulement. */
export const ENTRY_SIGNATURE_MAX_BYTES = 1024 * 1024;

// ─── Intervention planifiée (ajout 11) ──────────────────────────────────────


export const PlanInterventionInput = z.strictObject({
  customer: z.union([z.strictObject({ id: Id }), z.strictObject({ new: CustomerInput })]),
  vehicle: z.union([z.strictObject({ id: Id }), z.strictObject({ new: VehicleFields })]),
  /** Jour prévu dans le fuseau du garage (aujourd'hui ou plus tard, 422 sinon). */
  plannedFor: z.iso.date(),
  slot: PlannedSlot.nullable().optional(),
  estimatedMinutes: z.int().min(15).max(7 * 24 * 60).nullable().optional(),
  customerRequest: LongText.min(1),
  assignedMembershipId: Id.nullable().optional(),
  locationId: Id.nullable().optional(),
  /**
   * Intégration vague 7 (02/10/2026) : récapitulatif des travaux prévus et montant estimé TTC dès la prise de
   * rendez-vous, mêmes règles que `CreateInterventionInput` (montant : `amounts.view`, 403 sinon) ; repris à
   * l'« Entrée véhicule » (`CheckInInput`) si celle-ci ne les fournit pas.
   */
  plannedWork: PlannedWork.nullable().optional(),
  estimatedTotalTtc: Money.nullable().optional(),
});

export const UpdatePlannedInput = z.strictObject({
  plannedFor: z.iso.date().optional(),
  slot: PlannedSlot.nullable().optional(),
  estimatedMinutes: z.int().min(15).max(7 * 24 * 60).nullable().optional(),
  customerRequest: LongText.min(1).optional(),
  assignedMembershipId: Id.nullable().optional(),
  locationId: Id.nullable().optional(),
  /** Intégration vague 7 : `null` efface ; le montant (le fixer ou l'effacer) exige `amounts.view`. */
  plannedWork: PlannedWork.nullable().optional(),
  estimatedTotalTtc: Money.nullable().optional(),
});

export const CancelPlannedInput = z.strictObject({ reason: ShortText.min(3) });

/**
 * « Entrée véhicule » d'un planifié : `planned → checked_in`, crée l'intervention (`received`) avec
 * `receivedAt` (`checkReceivedAt`), kilométrage, conditions remises et figées, emplacement ; ouvre la fiche de
 * réception en brouillon. Une seule fois (409 ensuite).
 */
export const CheckInInput = z.strictObject({
  entryMileage: Mileage,
  termsAccepted: z.literal(true),
  receivedAt: Timestamp.optional(),
  customerRequest: LongText.min(1).optional(),
  entryConditionNotes: LongText.nullable().optional(),
  locationId: Id.nullable().optional(),
  assignedMembershipId: Id.nullable().optional(),
  /**
   * 02/10/2026 : récapitulatif des travaux prévus et montant estimé (mêmes règles que `CreateInterventionInput`).
   * Absents : ceux du rendez-vous sont repris ; `null` : aucun.
   */
  plannedWork: PlannedWork.nullable().optional(),
  estimatedTotalTtc: Money.nullable().optional(),
});


export const PlannedListQuery = z.strictObject({
  /** `upcoming` : à venir et en retard (défaut) ; `all` : y compris entrés et annulés. */
  scope: z.enum(['upcoming', 'all']).default('upcoming'),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
